/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect } from 'react';

interface FibblScriptLoaderProps {
    /**
     * Dev/staging token from app.fibbl.com → Tech settings.
     * Omit in production — domain-based auth is used automatically.
     * Never expose a real token on a publicly deployed site.
     */
    token?: string;
    /** CSP nonce from the root loader — required when the app enforces script-src nonces. */
    nonce?: string;
    /** BCP-47 locale forwarded to Fibbl's UI. Defaults to "en-US". */
    locale?: string;
}

/**
 * Injects the Fibbl CDN script once per page load.
 * Subsequent renders are no-ops thanks to the data-fibbl-config guard.
 * Must be rendered client-side only (the useEffect is the gate).
 */
export default function FibblScriptLoader({ token, nonce, locale = 'en-US' }: FibblScriptLoaderProps) {
    useEffect(() => {
        // Guard: Fibbl requires exactly one script tag with data-fibbl-config
        if (document.querySelector('[data-fibbl-config]')) return;

        const script = document.createElement('script');
        script.src = 'https://cdn.fibbl.com/fibbl.js';
        script.type = 'module';
        script.setAttribute('data-fibbl-config', '');
        script.setAttribute('data-locale', locale);
        if (token) script.setAttribute('data-token', token);
        if (nonce) script.nonce = nonce;
        document.head.appendChild(script);
        // Left in place for the session — Fibbl custom elements rely on it being present.
    }, [locale, nonce, token]);

    return null;
}
